import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Text, useBreakpointValue, useMediaQuery, useToast } from '@chakra-ui/react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import useStore from '../../../store';
import LabelDetail from '../../../components/LabelDetail';
import DownloadAndViewButton from '../../../components/Files/DownloadAndViewButton';
import getPositionLabel from '../../../utils/getPositionLabel';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import getDepartmentLabel from '../../../utils/getDepartmentLabel';

const QUERY_DETAIL = gql`
	query get($id: String!) {
		getPositionStatusById(id: $id) {
			id
			no
			rank
			position
			dateOfLastPosition
			dateOfLastRank
			department
			office
			organisation
			attachmentFile
			attachmentFileSize
			user {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				avatar
			}
			notification {
				id
				read
			}
		}
	}
`;

const MUTATION_READ_NOTIFICATION = gql`
	mutation read($id: String!) {
		readNotificationById(id: $id)
	}
`;

const LABEL_WIDTH = '300px';

const PositionStatusRankLabel = {
	Level_7: 'អនុមន្រ្តីកម្រិត ១',
	Level_6: 'អនុមន្រ្តីកម្រិត ២',
	Level_5: 'អនុមន្រ្តីកម្រិត ៣',
	Level_4: 'នាយក្រមការ',
	Level_3: 'ក្រមការដើមខ្សែ',
	Level_2: 'ក្រមការ',
	Level_1: 'កម្មសិក្សា',
};

function PositionStatusDetail({ id, onCloseModal }) {
	const { currentLang } = useStore((state) => state.currentLang);
	moment.locale(currentLang === 'kh' ? 'km' : 'en-gb');
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { countUnreadNotifications } = useStore((state) => state.countUnreadNotifications);
	const setCountUnreadNotifications = useStore((state) => state.setCountUnreadNotifications);
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const toast = useToast();
	const [record, setRecord] = useState(null);
	const [isBigScreen] = useMediaQuery(['(min-width: 992px) and (max-width: 1035px)']);
	const [loadDetail, { data }] = useLazyQuery(QUERY_DETAIL, { fetchPolicy: 'no-cache' });
	const [readNotification, {}] = useMutation(MUTATION_READ_NOTIFICATION);

	const showLargeScreen = useBreakpointValue({
		base: false,
		sm: false,
		md: true,
		lg: true, // >= 992px
		xl: true, // >= 1280px
	});

	useEffect(getDetail, [id]);

	useEffect(() => {
		const detail = data?.getPositionStatusById;

		setRecord(detail);
	}, [data]);

	function getDetail() {
		id && loadDetail({ variables: { id } });
	}

	function getCurrentWorkingStatusOfficeLabel(currentOffice) {
		if (currentOffice) {
			switch (currentOffice) {
				case 'office1':
					return currentLang === 'kh' ? 'ការិយាល័យរដ្ឋបាល' : 'Administrative';
				case 'office2':
					return currentLang === 'kh' ? 'ការិយាល័យគណនេយ្យ' : 'Accounting';
				case 'office3':
					return currentLang === 'kh' ? 'ការិយាល័យធនធានមនុស្ស' : 'Human Resource';
				case 'office4':
					return currentLang === 'kh' ? 'ការិយាល័យទំនាក់ទនងសាធារណៈ' : 'Public Relation';
				case 'office5':
					return currentLang === 'kh' ? 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ' : 'Licensing';
				case 'office6':
					return currentLang === 'kh' ? 'ការិយាល័យចុះបញ្ជីអន្តរការី' : 'Intermediary Registration';
				case 'office7':
					return currentLang === 'kh' ? 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ' : 'Legal and Mediation';
				case 'office8':
					return currentLang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurer';
				case 'office9':
					return currentLang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ' : 'General Insurer';
				case 'office10':
					return currentLang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង' : 'Micro Insurer';
				case 'office11':
					return currentLang === 'kh' ? 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន' : 'Prudential';
				case 'office12':
					return currentLang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurance Product';
				case 'office13':
					return currentLang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ' : 'General Insurance Product';
				case 'office14':
					return currentLang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច' : 'Micro Insurance Product';
				case 'office15':
					return currentLang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ' : 'Market Conduct Supervision';
				case 'office16':
					return currentLang === 'kh' ? 'ការិយាល័យស្រាវជ្រាវ' : 'Research';
				case 'office17':
					return currentLang === 'kh' ? 'ការិយាល័យបណ្តុះបណ្តាល' : 'Training';
				case 'office18':
					return currentLang === 'kh' ? 'ការិយាល័យសហប្រតិបត្តិការ' : 'Cooperation';
				case 'office19':
					return currentLang === 'kh' ? 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង' : 'InsurTech';
				default:
					return '';
			}
		}
	}

	useEffect(() => {
		if (record?.notification?.read === false) {
			setCountUnreadNotifications(countUnreadNotifications - 1);
			readNotification({ variables: { id: record?.notification?.id } });
		}
	}, [record]);

	const hideOfficeDepartment = record?.position === '1' || record?.position === '2';

	return (
		<>
			<Box w="100%">
				<LabelDetail
					mb={1}
					mt={2}
					labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
					direction="row"
					label="Number"
					titleDetail={record?.no ? String(record?.no).padStart(4, '0') : 'N/A'}
				/>

				<LabelDetail
					mb={1}
					mt={2}
					labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
					direction="row"
					label="Officials"
					titleDetail={currentLang === 'en' ? record?.user?.fullName : record?.user?.fullNameKhmer}
				/>

				<LabelDetail
					mb={1}
					mt={2}
					labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
					direction="row"
					label="Rank"
					titleDetail={record?.rank ? PositionStatusRankLabel[record?.rank] : 'N/A'}
				/>

				<LabelDetail
					mb={1}
					mt={2}
					labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
					direction="row"
					label="Organisation"
					titleDetail={currentLang === 'en' ? 'Insurance Regulator of Cambodia' : 'និយ័តករធានារ៉ាប់រងកម្ពុជា'}
				/>

				<LabelDetail
					mb={1}
					mt={2}
					labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
					direction="row"
					label="Position"
					titleDetail={record?.position ? getPositionLabel(record?.position, 'en') : 'N/A'}
				/>

				<LabelDetail
					mb={1}
					mt={2}
					labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
					direction="row"
					label="Date of Last Position"
					titleDetail={record?.dateOfLastPosition ? moment(record?.dateOfLastPosition).format('LLLL') : 'N/A'}
				/>
				<LabelDetail
					mb={1}
					mt={2}
					labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
					direction="row"
					label="Date of Last Rank"
					titleDetail={record?.dateOfLastRank ? moment(record?.dateOfLastRank).format('LLLL') : 'N/A'}
				/>

				{!hideOfficeDepartment && (
					<LabelDetail
						mb={1}
						mt={2}
						labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
						direction="row"
						label="Department"
						titleDetail={record?.department && record.department !== 'N/A' ? getDepartmentLabel(record?.department) : 'N/A'}
					/>
				)}

				{!hideOfficeDepartment && (
					<LabelDetail
						mb={1}
						mt={2}
						labelWidth={showLargeScreen ? LABEL_WIDTH : '150px'}
						direction="row"
						label="Office"
						titleDetail={record?.office && record.office !== 'N/A' ? getCurrentWorkingStatusOfficeLabel(record.office) : 'N/A'}
					/>
				)}
				<Flex mt={3}>
					<Text fontWeight="semibold" w={record?.attachmentFile ? '300px' : '316px'} flexShrink={0}>
						{t('Attachment File')}
					</Text>

					<Box flex={1}>
						{record?.attachmentFile ? (
							<DownloadAndViewButton url={record?.attachmentFile?.file} size={record?.attachmentFile?.size} />
						) : (
							<Text>N/A</Text>
						)}
					</Box>
				</Flex>
			</Box>
		</>
	);
}

export default PositionStatusDetail;
